import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { NewAccountStackParamList, NewAccountStackRoutes } from './type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { errorParser } from '@/features/bill-payment/screens/otp/type';

export interface NewAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
}

export const NewAccountWebOneScreen: React.FC<NewAccountProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, onChangeView } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;
  const [loading, setLoading] = useState(false);

  const navigation = useNavigation<NativeStackNavigationProp<NewAccountStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account-management::credit-card-new-account.new-account-confirmation.title--new-account-instruction-confirmation')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <View sx={body}>
        <TextBody semiBold>
          {t('card-account-management::credit-card-new-account.new-account-confirmation.title--create-new-account-inquiry')}
        </TextBody>
        <View>
          <TextCaption sx={textTitle} semiBold>
            {t('card-account-management::credit-card-new-account.glossary.account-name')}
          </TextCaption>
          <H6 sx={textAtmPin}>{data.name}</H6>
        </View>
        <View>
          <TextCaption sx={textTitle} semiBold>
            {t('card-account-management::credit-card-new-account.glossary.assets')}
          </TextCaption>
          <H6 sx={textAtmPin}>{data.assets.join(' , ')}</H6>
        </View>
        {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            disabled={loading}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::set-card-limit.button--back')}
            onPress={() => {
              navigation?.goBack();
              onChangeView && onChangeView();
            }}
          />
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            loading={loading}
            label={t('card-account-management::general.button--place-instruction')}
            onPress={() => {
              if (func) {
                setLoading(true);
                func(
                  data,
                  (returnData) => {
                    setLoading(false);
                    onReturnData && onReturnData(returnData.data);
                    navigation.navigate(NewAccountStackRoutes.Placed, {
                      onLayout: onLayout,
                      data,
                      returnData: returnData.data,
                      refresh
                    });
                  },
                  (error) => {
                    setLoading(false);
                    const data: any = error.response?.data;
                    const errorMessage =
                      data && isString(data.message?.code) ? errorParser(data.message?.code, t) : errorParser(error.message, t);
                    setAlertMessage(errorMessage);
                  }
                );
              }
            }}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 20
  },
  btn: {
    width: 216,
    height: 44
  }
};
